import React, { useState, useMemo } from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  Button,
  Space,
  Tag,
  Image,
  Skeleton,
  message,
} from 'antd';
import {
  SearchOutlined,
  EditOutlined,
  CopyOutlined,
  DeleteOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import type {
  MenuDish,
  MenuCategory,
  DishStatus,
  StockStatus,
} from '../../../types/menu';
import type { PatchDishPayload } from '../../../types/menu';
import { BulkActionBar } from './BulkActionBar';
import { EditDishDrawer } from './EditDishDrawer';
import { formatVnDate } from '../../../utils/vnDateTime';

export type DishSortField = 'updatedAt' | 'price' | 'name';
export type DishSortOrder = 'asc' | 'desc';

export interface DishTableProps {
  dishes: MenuDish[];
  categories: MenuCategory[];
  loading?: boolean;
  selectedCategoryId: string | null;
  onPatchDish: (dishId: string, payload: PatchDishPayload) => Promise<void>;
  onPatchBulk: (payload: {
    dishIds: string[];
    patch: {
      status?: DishStatus;
      stockStatus?: StockStatus;
      categoryId?: string | null;
      priceDelta?: number;
      pricePercent?: number;
    };
    adminNote: string;
  }) => Promise<void>;
  onDuplicateDish: (dish: MenuDish) => void;
  onDeleteDish: (dish: MenuDish, adminNote: string) => Promise<void>;
  onConfirmWithNote: (
    action: string,
    fn: (note: string) => Promise<void>
  ) => void;
  onOpenAdminNoteModal: (
    title: string,
    onConfirm: (note: string) => Promise<void>
  ) => void;
}

interface InlineEditState {
  dishId: string;
  name?: string;
  price?: number;
  discountAmount?: number | null;
  discountPercent?: number | null;
  status?: DishStatus;
  stockStatus?: StockStatus;
  categoryId?: string | null;
}

function formatPrice(n: number): string {
  return new Intl.NumberFormat('vi-VN').format(n) + '₫';
}

function formatDiscount(dish: MenuDish): string {
  if (dish.discountAmount != null && dish.discountAmount > 0) {
    return `-${formatPrice(dish.discountAmount)}`;
  }
  if (dish.discountPercent != null && dish.discountPercent > 0) {
    return `-${dish.discountPercent}%`;
  }
  return '–';
}

export const DishTable: React.FC<DishTableProps> = ({
  dishes,
  categories,
  loading = false,
  selectedCategoryId,
  onPatchDish,
  onPatchBulk,
  onDuplicateDish,
  onDeleteDish,
  onConfirmWithNote,
  onOpenAdminNoteModal,
}) => {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [stockFilter, setStockFilter] = useState<string>('all');
  const [priceMin, setPriceMin] = useState<number | undefined>();
  const [priceMax, setPriceMax] = useState<number | undefined>();
  const [sortField, setSortField] = useState<DishSortField>('updatedAt');
  const [sortOrder, setSortOrder] = useState<DishSortOrder>('desc');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [inlineEdit, setInlineEdit] = useState<InlineEditState | null>(null);
  const [editDrawerDish, setEditDrawerDish] = useState<MenuDish | null>(null);

  const filteredAndSortedDishes = useMemo(() => {
    let result = [...dishes];

    if (selectedCategoryId && selectedCategoryId !== '__all__') {
      result = result.filter(d => d.categoryId === selectedCategoryId);
    }

    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(d => d.name.toLowerCase().includes(q));
    }

    if (statusFilter !== 'all') {
      result = result.filter(d => d.status === statusFilter);
    }
    if (stockFilter !== 'all') {
      result = result.filter(d => d.stockStatus === stockFilter);
    }
    if (priceMin != null) {
      result = result.filter(d => d.price >= priceMin);
    }
    if (priceMax != null) {
      result = result.filter(d => d.price <= priceMax);
    }

    result.sort((a, b) => {
      const mul = sortOrder === 'asc' ? 1 : -1;
      if (sortField === 'updatedAt') {
        return (
          mul *
          (new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime())
        );
      }
      if (sortField === 'price') {
        return mul * (a.price - b.price);
      }
      if (sortField === 'name') {
        return mul * a.name.localeCompare(b.name);
      }
      return 0;
    });

    return result;
  }, [
    dishes,
    selectedCategoryId,
    search,
    statusFilter,
    stockFilter,
    priceMin,
    priceMax,
    sortField,
    sortOrder,
  ]);

  const handleSaveInline = (dish: MenuDish) => {
    if (!inlineEdit || inlineEdit.dishId !== dish.id) return;
    const payload: PatchDishPayload = {
      adminNote: '',
    };
    let hasChange = false;
    if (inlineEdit.name !== undefined && inlineEdit.name !== dish.name) {
      payload.name = inlineEdit.name;
      hasChange = true;
    }
    if (inlineEdit.price !== undefined && inlineEdit.price !== dish.price) {
      payload.price = inlineEdit.price;
      hasChange = true;
    }
    if (
      inlineEdit.discountAmount !== undefined &&
      inlineEdit.discountAmount !== dish.discountAmount
    ) {
      payload.discountAmount = inlineEdit.discountAmount;
      hasChange = true;
    }
    if (
      inlineEdit.discountPercent !== undefined &&
      inlineEdit.discountPercent !== dish.discountPercent
    ) {
      payload.discountPercent = inlineEdit.discountPercent;
      hasChange = true;
    }
    if (inlineEdit.status && inlineEdit.status !== dish.status) {
      payload.status = inlineEdit.status;
      hasChange = true;
    }
    if (inlineEdit.stockStatus && inlineEdit.stockStatus !== dish.stockStatus) {
      payload.stockStatus = inlineEdit.stockStatus;
      hasChange = true;
    }
    if (
      inlineEdit.categoryId !== undefined &&
      inlineEdit.categoryId !== dish.categoryId
    ) {
      payload.categoryId = inlineEdit.categoryId;
      hasChange = true;
    }

    if (!hasChange) {
      setInlineEdit(null);
      return;
    }

    onOpenAdminNoteModal('Confirm changes', async note => {
      await onPatchDish(dish.id, { ...payload, adminNote: note });
      message.success(`Đã cập nhật món ăn "${dish.name}"`);
      setInlineEdit(null);
    });
  };

  const handleDiscardInline = () => {
    setInlineEdit(null);
  };

  const hasUnsavedChanges = (dish: MenuDish) => {
    if (!inlineEdit || inlineEdit.dishId !== dish.id) return false;
    return (
      (inlineEdit.name !== undefined && inlineEdit.name !== dish.name) ||
      (inlineEdit.price !== undefined && inlineEdit.price !== dish.price) ||
      (inlineEdit.discountAmount !== undefined &&
        inlineEdit.discountAmount !== dish.discountAmount) ||
      (inlineEdit.discountPercent !== undefined &&
        inlineEdit.discountPercent !== dish.discountPercent) ||
      (inlineEdit.status && inlineEdit.status !== dish.status) ||
      (inlineEdit.stockStatus && inlineEdit.stockStatus !== dish.stockStatus) ||
      (inlineEdit.categoryId !== undefined &&
        inlineEdit.categoryId !== dish.categoryId)
    );
  };

  const columns = [
    {
      title: '',
      key: 'thumbnail',
      width: 56,
      render: (_: unknown, record: MenuDish) =>
        record.thumbnailUrl ? (
          <Image
            src={record.thumbnailUrl}
            alt=""
            width={40}
            height={40}
            style={{ objectFit: 'cover', borderRadius: 8 }}
          />
        ) : (
          <div
            style={{
              width: 40,
              height: 40,
              background: '#f3f4f6',
              borderRadius: 8,
            }}
          />
        ),
    },
    {
      title: 'Tên món ăn',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: (sortField === 'name'
        ? sortOrder === 'asc'
          ? 'ascend'
          : 'descend'
        : undefined) as 'ascend' | 'descend' | undefined,
      render: (name: string, record: MenuDish) => {
        const isEditing = inlineEdit?.dishId === record.id;
        return isEditing ? (
          <Input
            size="small"
            value={inlineEdit.name ?? name}
            onChange={e =>
              setInlineEdit(prev =>
                prev && prev.dishId === record.id
                  ? { ...prev, name: e.target.value }
                  : prev
              )
            }
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <span
            className="merchant-menu-dish-name-cell"
            onClick={() =>
              setInlineEdit({
                dishId: record.id,
                name: record.name,
                price: record.price,
                discountAmount: record.discountAmount,
                discountPercent: record.discountPercent,
                status: record.status,
                stockStatus: record.stockStatus,
                categoryId: record.categoryId,
              })
            }
          >
            {name}
            {hasUnsavedChanges(record) && (
              <span className="merchant-menu-unsaved-dot" title="Unsaved" />
            )}
          </span>
        );
      },
    },
    {
      title: 'Price',
      dataIndex: 'price',
      key: 'price',
      width: 120,
      sorter: true,
      sortOrder: (sortField === 'price'
        ? sortOrder === 'asc'
          ? 'ascend'
          : 'descend'
        : undefined) as 'ascend' | 'descend' | undefined,
      render: (price: number, record: MenuDish) => {
        const isEditing = inlineEdit?.dishId === record.id;
        return isEditing ? (
          <Input
            size="small"
            type="number"
            value={inlineEdit.price ?? price}
            onChange={e =>
              setInlineEdit(prev =>
                prev && prev.dishId === record.id
                  ? { ...prev, price: Number(e.target.value) || 0 }
                  : prev
              )
            }
            onClick={e => e.stopPropagation()}
          />
        ) : (
          formatPrice(price)
        );
      },
    },
    {
      title: 'Discount',
      key: 'discount',
      width: 100,
      render: (_: unknown, record: MenuDish) => {
        const isEditing = inlineEdit?.dishId === record.id;
        return isEditing ? (
          <Input
            size="small"
            placeholder="% or amount"
            value={
              inlineEdit.discountPercent != null
                ? `${inlineEdit.discountPercent}%`
                : inlineEdit.discountAmount ?? ''
            }
            onChange={e => {
              const v = e.target.value;
              const pct = v.match(/^(\d+)%?$/);
              if (pct) {
                setInlineEdit(prev =>
                  prev && prev.dishId === record.id
                    ? {
                        ...prev,
                        discountPercent: Number(pct[1]),
                        discountAmount: null,
                      }
                    : prev
                );
              } else {
                const num = parseInt(v, 10);
                setInlineEdit(prev =>
                  prev && prev.dishId === record.id
                    ? {
                        ...prev,
                        discountAmount: Number.isNaN(num) ? null : num,
                        discountPercent: null,
                      }
                    : prev
                );
              }
            }}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          formatDiscount(record)
        );
      },
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: DishStatus, record: MenuDish) => {
        const isEditing = inlineEdit?.dishId === record.id;
        return isEditing ? (
          <Select
            size="small"
            value={inlineEdit.status ?? status}
            onChange={v =>
              setInlineEdit(prev =>
                prev && prev.dishId === record.id
                  ? { ...prev, status: v }
                  : prev
              )
            }
            options={[
              { value: 'PUBLISHED', label: 'Published' },
              { value: 'HIDDEN', label: 'Hidden' },
            ]}
            style={{ width: 110 }}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <Tag color={status === 'PUBLISHED' ? 'green' : 'default'}>
            {status === 'PUBLISHED' ? 'Published' : 'Hidden'}
          </Tag>
        );
      },
    },
    {
      title: 'Stock',
      dataIndex: 'stockStatus',
      key: 'stockStatus',
      width: 100,
      render: (stock: StockStatus, record: MenuDish) => {
        const isEditing = inlineEdit?.dishId === record.id;
        return isEditing ? (
          <Select
            size="small"
            value={inlineEdit.stockStatus ?? stock}
            onChange={v =>
              setInlineEdit(prev =>
                prev && prev.dishId === record.id
                  ? { ...prev, stockStatus: v }
                  : prev
              )
            }
            options={[
              { value: 'IN_STOCK', label: 'In stock' },
              { value: 'OUT_OF_STOCK', label: 'Out of stock' },
            ]}
            style={{ width: 110 }}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <Tag color={stock === 'IN_STOCK' ? 'green' : 'red'}>
            {stock === 'IN_STOCK' ? 'In stock' : 'Out of stock'}
          </Tag>
        );
      },
    },
    {
      title: 'Category',
      key: 'categoryId',
      width: 140,
      render: (_: unknown, record: MenuDish) => {
        const isEditing = inlineEdit?.dishId === record.id;
        const cat = categories.find(c => c.id === record.categoryId);
        return isEditing ? (
          <Select
            size="small"
            value={inlineEdit.categoryId ?? record.categoryId ?? undefined}
            onChange={v =>
              setInlineEdit(prev =>
                prev && prev.dishId === record.id
                  ? { ...prev, categoryId: v || null }
                  : prev
              )
            }
            allowClear
            options={categories.map(c => ({ value: c.id, label: c.name }))}
            style={{ width: 120 }}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          cat?.name ?? '–'
        );
      },
    },
    {
      title: 'Updated at',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 120,
      sorter: true,
      sortOrder: (sortField === 'updatedAt'
        ? sortOrder === 'asc'
          ? 'ascend'
          : 'descend'
        : undefined) as 'ascend' | 'descend' | undefined,
      render: (d: string) => (d ? formatVnDate(d, 'DD MMM YYYY') : '–'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      fixed: 'right' as const,
      render: (_: unknown, record: MenuDish) => {
        const isEditing = inlineEdit?.dishId === record.id;
        return (
          <Space size="small" onClick={e => e.stopPropagation()}>
            {isEditing ? (
              <>
                <Button
                  type="link"
                  size="small"
                  icon={<SaveOutlined />}
                  onClick={() => handleSaveInline(record)}
                >
                  Save
                </Button>
                <Button
                  type="link"
                  size="small"
                  icon={<CloseOutlined />}
                  onClick={handleDiscardInline}
                >
                  Discard
                </Button>
              </>
            ) : (
              <>
                <Button
                  type="text"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => setEditDrawerDish(record)}
                />
                <Button
                  type="text"
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => onDuplicateDish(record)}
                />
                <Button
                  type="text"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() =>
                    onConfirmWithNote('Delete dish', note =>
                      onDeleteDish(record, note)
                    )
                  }
                />
              </>
            )}
          </Space>
        );
      },
    },
  ];

  const handleTableChange = (
    pagination: unknown,
    filters: unknown,
    sorter: unknown
  ) => {
    const s = sorter as { field?: string; order?: string };
    if (s?.field) {
      setSortField(s.field as DishSortField);
      setSortOrder((s.order === 'ascend' ? 'asc' : 'desc') as DishSortOrder);
    }
  };

  const selectedDishes = useMemo(
    () => dishes.filter(d => selectedRowKeys.includes(d.id)),
    [dishes, selectedRowKeys]
  );

  const handleBulkPublish = async (ids: string[], adminNote: string) => {
    await onPatchBulk({
      dishIds: ids,
      patch: { status: 'PUBLISHED' },
      adminNote,
    });
    message.success(`Published ${ids.length} dish(es)`);
    setSelectedRowKeys([]);
  };
  const handleBulkHide = async (ids: string[], adminNote: string) => {
    await onPatchBulk({ dishIds: ids, patch: { status: 'HIDDEN' }, adminNote });
    message.success(`Hidden ${ids.length} dish(es)`);
    setSelectedRowKeys([]);
  };
  const handleBulkStock = async (
    ids: string[],
    stockStatus: StockStatus,
    adminNote: string
  ) => {
    await onPatchBulk({ dishIds: ids, patch: { stockStatus }, adminNote });
    message.success(`Updated stock for ${ids.length} dish(es)`);
    setSelectedRowKeys([]);
  };
  const handleBulkPrice = async (
    ids: string[],
    delta?: number,
    percent?: number,
    adminNote?: string
  ) => {
    if (!adminNote) return;
    await onPatchBulk({
      dishIds: ids,
      patch: delta != null ? { priceDelta: delta } : { pricePercent: percent },
      adminNote,
    });
    message.success(`Updated price for ${ids.length} dish(es)`);
    setSelectedRowKeys([]);
  };
  const handleBulkMoveCategory = async (
    ids: string[],
    categoryId: string | null,
    adminNote: string
  ) => {
    await onPatchBulk({ dishIds: ids, patch: { categoryId }, adminNote });
    message.success(`Moved ${ids.length} dish(es)`);
    setSelectedRowKeys([]);
  };

  if (loading) {
    return (
      <Card className="merchant-menu-dish-card">
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  return (
    <Card className="merchant-menu-dish-card">
      <div className="merchant-menu-dish-toolbar">
        <Input
          placeholder="Tìm kiếm theo tên món ăn"
          prefix={<SearchOutlined />}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: 220 }}
          allowClear
        />
        <Select
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'PUBLISHED', label: 'Published' },
            { value: 'HIDDEN', label: 'Hidden' },
          ]}
          style={{ width: 120 }}
        />
        <Select
          value={stockFilter}
          onChange={setStockFilter}
          options={[
            { value: 'all', label: 'All Stock' },
            { value: 'IN_STOCK', label: 'In stock' },
            { value: 'OUT_OF_STOCK', label: 'Out of stock' },
          ]}
          style={{ width: 120 }}
        />
        <Input
          placeholder="Min price"
          type="number"
          value={priceMin ?? ''}
          onChange={e =>
            setPriceMin(e.target.value ? Number(e.target.value) : undefined)
          }
          style={{ width: 100 }}
        />
        <Input
          placeholder="Max price"
          type="number"
          value={priceMax ?? ''}
          onChange={e =>
            setPriceMax(e.target.value ? Number(e.target.value) : undefined)
          }
          style={{ width: 100 }}
        />
      </div>

      <BulkActionBar
        selectedIds={selectedRowKeys as string[]}
        dishes={selectedDishes}
        categories={categories}
        onBulkPublish={handleBulkPublish}
        onBulkHide={handleBulkHide}
        onBulkStock={handleBulkStock}
        onBulkPrice={handleBulkPrice}
        onBulkMoveCategory={handleBulkMoveCategory}
        onClearSelection={() => setSelectedRowKeys([])}
        onConfirmWithNote={onConfirmWithNote}
      />

      <Table
        rowSelection={{
          selectedRowKeys,
          onChange: keys => setSelectedRowKeys(keys as string[]),
        }}
        columns={columns}
        dataSource={filteredAndSortedDishes}
        rowKey="id"
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: total => `Total ${total} dishes`,
        }}
        onChange={handleTableChange}
        scroll={{ x: 1100 }}
      />

      <EditDishDrawer
        open={!!editDrawerDish}
        dish={editDrawerDish}
        categories={categories}
        onClose={() => setEditDrawerDish(null)}
        onSave={async (dishId, values, note) => {
          await onPatchDish(dishId, { ...values, adminNote: note });
          message.success('Đã cập nhật món ăn');
        }}
        onOpenAdminNoteModal={onOpenAdminNoteModal}
      />
    </Card>
  );
};
